import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  RefreshCw, Truck, PackageCheck, Users, Layers, ArrowUpRight, ArrowDownRight, X,
  Phone, Mail, MapPin, ChevronLeft, ChevronRight,
  Wallet, FileText, ShoppingCart, TrendingUp,Cylinder,Check,
  BoxIcon,Clock,AlertTriangle,Plus
} from "lucide-react";
import { CurrencyRupee } from "@mui/icons-material";
import { BsJournalBookmarkFill } from "react-icons/bs";
import { Skeleton, Box, Tabs, Tab } from "@mui/material";
import api from "../../api/axiosInstance";
import { financialDashboardApi } from "../../api";
import CRMDashboard from "../CRM/CRMDashboard";
import appPreloader from "../../services/appPreloader";
import { BASE_URL } from "../../config/apiconfig";
import { getCustomerActivity } from "../../api/customerActivityApi";
import { TokenManager } from "../../utils/tokenManager";
import { planAtLeast } from "../../utils/planAccess";
import "./Dashboard.css";
//import { text } from "body-parser";

const initialStats = [
  { label: "Total delivered", value: 0, delta: "+0%", up: true, accent: "#0F6E56", Icon: Truck, route: "/dashboard/challans/view", filter: "DELIVERED" },
  { label: "Total received", value: 0, delta: "+0%", up: true, accent: "#B45309", Icon: PackageCheck, route: "/dashboard/challans/view", filter: "RECEIVED" },
  { label: "Total combined", value: 0, delta: "+0%", up: true, accent: "#5B4B8A", Icon: Layers, route: "/dashboard/challans/view", filter: "COMBINED" },
  { label: "Total customers", value: 0, delta: "+0%", up: true, accent: "#3B4F6B", Icon: Users, route: "/dashboard/customers" },
  // --- NEW 4 CARDS WITH DUMMY DATA ---
{ label: "Total Receivable", value: 1248320, isRupee: true, subtext: "₹2.18L overdue", delta: "8.4%", up: true, accent: "#2563EB", Icon: Wallet, route: "/dashboard/invoices/view", planRequired: "silver" },
   { label: "Total Payable", value: 726840, isRupee: true, subtext: "12 open bills", delta: "", up: true, accent: "#DC2626", Icon: FileText, route: "/dashboard/bills", planRequired: "silver" },
   { label: "Total Sales", value: 2864500, isRupee: true, subtext: "vs. last month", delta: "12.6%", up: true, accent: "#059669", Icon: TrendingUp, route: "/dashboard/sales-orders", planRequired: "silver" },
   { label: "Total Purchases", value: 1692750, isRupee: true, subtext: "vs. last month", delta: "4.2%", up: true, accent: "#7C3AED", Icon: ShoppingCart, route: "/dashboard/purchases", planRequired: "silver" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="customTooltip">
      <div className="customTooltipLabel">{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="customTooltipValue">
          {item.name}: {item.value}
        </div>
      ))}
    </div>
  );
};


const AnimatedRefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <g>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="1"
        fill="freeze" />
    </g>
  </svg>
);

// const RupeeIcon = ({ size = 12 }) => (
//   <svg 
//     xmlns="http://www.w3.org/2000/svg" 
//     width={size} 
//     height={size} 
//     fill="currentColor" 
//     viewBox="0 0 16 16"
//   >
//     <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
//   </svg>
// );

export default function DashboardRedesign() {
  const navigate = useNavigate();
  const currentPlan = useMemo(() => TokenManager.getPlan(), []);
  const canUseCrm = planAtLeast(currentPlan, "gold");
  const canUseFinance = planAtLeast(currentPlan, "silver");
  const [activeSection, setActiveSection] = useState(0);
  const [crmRefreshKey, setCrmRefreshKey] = useState(0);
  const [showProductsPopup, setShowProductsPopup] = useState(false);
  const [showCustomersPopup, setShowCustomersPopup] = useState(false);
  const [stats, setStats] = useState(() => initialStats.filter((s) => !s.planRequired || planAtLeast(currentPlan, s.planRequired)));
  const [productSummaries, setProductSummaries] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [customerSummaries, setCustomerSummaries] = useState([]);
  const [customerMetricsLoading, setCustomerMetricsLoading] = useState({});
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isFetchingDashboardRef = useRef(false);
  const isFetchingCustomersRef = useRef(false);
  const isMountedRef = useRef(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [customerPage, setCustomerPage] = useState(0);
  const customersPerPage = 4;
  const [greetingBurstActive, setGreetingBurstActive] = useState(false);
  const [greetingBurstItems, setGreetingBurstItems] = useState([]);
  const [addressTooltip, setAddressTooltip] = useState(null);
  const tapTimeoutRef = useRef(null);
  
  // Real-time data states
  const [needsAttention, setNeedsAttention] = useState(null);
  const [cylinderTracking, setCylinderTracking] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentBills, setRecentBills] = useState([]);
  const getTimeBasedMessage = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return "Good morning • Welcome back. Here's today's business overview.";
    } else if (hour >= 12 && hour < 17) {
      return "Good afternoon • Track today's operations and performance.";
    } else if (hour >= 17 && hour < 21) {
      return "Good evening • Review today's activity and insights.";
    } else {
      return "Good night • Your dashboard is up to date.";
    }
  };

  const getHeaderSubtitle = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12)
      return "Start the day with a complete view of your business operations.";

    if (hour >= 12 && hour < 17)
      return "Keep track of deliveries, inventory, and customer activity.";

    if (hour >= 17 && hour < 21)
      return "Review today's performance and outstanding tasks.";

    return "All business data has been updated and is ready for review.";
  };

  const getStatusClass = (status) => {
    if (!status) return 'gray';
    const s = status.toLowerCase();
    if (s === 'paid' || s === 'completed') return 'green';
    if (s === 'overdue') return 'red';
    if (s === 'partially paid' || s === 'partial') return 'yellow';
    if (s === 'outstanding' || s === 'open' || s === 'pending') return 'yellow';
    if (s === 'draft') return 'blue';
    return 'gray';
  };

  const viewPassbook = (customerId, customerName, customerEmail) => {
    navigate('/dashboard/customers/passbook', {
      state: {
        customerId,
        customerName,
        customerEmail
      }
    });
  };

  const showAddressTooltip = (event) => {
    const el = event.currentTarget;
    const address = el.dataset.tooltip;
    if (!address || address === "N/A") return;

    const rect = el.getBoundingClientRect();
    const tooltipWidth = Math.min(320, window.innerWidth - 32);
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - tooltipWidth - 16));
    const estimatedHeight = 54;
    const showAbove = rect.bottom + estimatedHeight + 8 > window.innerHeight;

    setAddressTooltip({
      text: address,
      top: showAbove ? Math.max(16, rect.top - estimatedHeight - 8) : rect.bottom + 6,
      left,
      width: tooltipWidth,
    });
  };

  const hideAddressTooltip = () => setAddressTooltip(null);

  const addressTooltipPortal = addressTooltip && typeof document !== "undefined"
    ? createPortal(
        <div
          className="customerAddressTooltip"
          style={{
            top: `${addressTooltip.top}px`,
            left: `${addressTooltip.left}px`,
            width: `${addressTooltip.width}px`,
          }}
        >
          {addressTooltip.text}
        </div>,
        document.body
      )
    : null;

  const handleCustomerPageChange = (newPage) => {
    if (
      newPage < 0 ||
      newPage >= getTotalCustomerPages()
    ) {
      return;
    }

    setCustomerPage(newPage);
  };

  const handleOpenCustomersPopup = async () => {
    setShowCustomersPopup(true);

    if (!customersLoaded) {
      await fetchCustomerSummaries();
    }

    setCustomerPage(0);

    // Load metrics for visible customers if not already loaded
    loadVisibleCustomerMetrics().catch(error => {
      console.error('Failed to load customer metrics:', error);
    });
  };

  const triggerGreetingBurst = (event) => {
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }

    const targetRect = event?.currentTarget?.getBoundingClientRect?.();
    const touchPoint = event?.changedTouches?.[0] || event?.touches?.[0];
    const clientX = event?.clientX ?? touchPoint?.clientX ?? (targetRect ? targetRect.left + targetRect.width / 2 : 0);
    const clientY = event?.clientY ?? touchPoint?.clientY ?? (targetRect ? targetRect.top + targetRect.height / 2 : 0);
    const emojis = ["✨", "🌟", "🎉", "💫", "⭐", "⚡", "🥳", "🎊", "💥", "🎈"];
    const items = Array.from({ length: 20 }, (_, index) => {
      const angle = (Math.PI * 0.9 * index) / 19 + (Math.random() * 0.3 - 0.15);
      const distance = 220 + Math.random() * 180;
      return {
        id: `${Date.now()}-${index}`,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        left: clientX,
        top: clientY,
        x: Math.cos(angle) * distance,
        y: -Math.abs(Math.sin(angle) * distance) - 180,
        delay: index * 30,
        duration: 1100 + Math.random() * 250,
        size: 34 + Math.random() * 24,
      };
    });

    setGreetingBurstItems(items);
    setGreetingBurstActive(true);

    tapTimeoutRef.current = setTimeout(() => {
      setGreetingBurstActive(false);
      setGreetingBurstItems([]);
    }, 1000);
  };

  const handleDateDoubleClick = (e) => {
    triggerGreetingBurst(e);
  };

  const getCurrentPageCustomers = () => {
    const startIndex = customerPage * customersPerPage;
    const endIndex = startIndex + customersPerPage;
    return customerSummaries.slice(startIndex, endIndex);
  };

  const getTotalCustomerPages = () => {
    return Math.ceil(customerSummaries.length / customersPerPage);
  };

  const fetchCustomerSummaries = async (forceRefresh = false) => {
    if (isFetchingCustomersRef.current) return;
    isFetchingCustomersRef.current = true;
    setLoadingCustomers(true);

    try {
      const response = await api.get("customers/search", {
        params: {
          page: 0,
          size: 50,
          name: "",
          email: ""
        }
      });

      const customers = response.data?.content || [];

      let customerData = customers.map((customer) => ({
        id: customer.id,
        name: customer.name,
        mobile: customer.mobile || customer.contactNumber || "N/A",
        email: customer.email || "N/A",
        address: customer.address || "N/A",
        runningBalance: customer.runningBalance || 0,
        balance: null,
        totalTransactions: null,
        delivered: null,
        received: null,
        metricsLoaded: false
      }));

      // If force refresh is true, fetch fresh running balance for each customer
      if (forceRefresh) {
        const customerIds = customerData.map(c => c.id);
        const freshBalancePromises = customerIds.map(id => 
          api.get(`customers/getById/${id}`).then(response => {
            console.log(`Customer ${id} API response:`, response.data);
            return response;
          }).catch(() => ({ data: { runningBalance: 0 } }))
        );
        
        const freshBalanceResponses = await Promise.all(freshBalancePromises);
        
        customerData = customerData.map((customer, index) => {
          const apiData = freshBalanceResponses[index]?.data || {};
          console.log(`Customer ${customer.id} - API runningBalance:`, apiData.runningBalance);
          return {
            ...customer,
            runningBalance: apiData.runningBalance !== undefined ? apiData.runningBalance : customer.runningBalance
          };
        });
      }

      setCustomerSummaries(customerData);
      setCustomersLoaded(true);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setCustomerSummaries([]);
    } finally {
      isFetchingCustomersRef.current = false;
      setLoadingCustomers(false);
    }
  };

  const fetchCustomerMetrics = async (customer) => {
    if (!customer?.id) return;

    setCustomerMetricsLoading((prev) => ({
      ...prev,
      [customer.id]: true
    }));

    try {
      const cacheKey = `customerActivity_${customer.id}`;
      const cached = appPreloader.getCachedData(cacheKey);

      if (cached) {
        setCustomerSummaries((prev) =>
          prev.map((item) =>
            item.id === customer.id
              ? {
                  ...item,
                  ...cached,
                  metricsLoaded: true
                }
              : item
          )
        );

        setCustomerMetricsLoading((prev) => ({
          ...prev,
          [customer.id]: false
        }));

        return;
      }

      const metrics =
        await getCustomerActivity({
          customerId: customer.id,
          customerName: customer.name,
          customerEmail: customer.email
        });

      setCustomerSummaries((prev) =>
        prev.map((item) =>
          item.id === customer.id
            ? {
                ...item,
                ...metrics,
                metricsLoaded: true
              }
            : item
        )
      );

      appPreloader.cache.set(cacheKey, metrics);
      appPreloader.saveToLocalStorage();

    } catch (error) {
      console.error(
        `Failed to fetch metrics for ${customer.name}`,
        error
      );

      setCustomerSummaries((prev) =>
        prev.map((item) =>
          item.id === customer.id
            ? {
                ...item,
                metricsLoaded: true,
                balance: 0,
                totalTransactions: 0,
                delivered: 0,
                received: 0
              }
            : item
        )
      );
    } finally {
      setCustomerMetricsLoading((prev) => ({
        ...prev,
        [customer.id]: false
      }));
    }
  };

  const loadVisibleCustomerMetrics = async () => {
    const visibleCustomers = getCurrentPageCustomers();

    const customersToLoad =
      visibleCustomers.filter(
        (customer) => !customer.metricsLoaded
      );

    if (!customersToLoad.length) {
      return;
    }

    await Promise.all(
      customersToLoad.map(
        (customer) =>
          fetchCustomerMetrics(customer)
      )
    );
  };

  const normalizeChallanList = (raw) => {
    const content = Array.isArray(raw)
      ? raw
      : raw?.content ?? raw?.data?.content ?? raw?.result?.content ?? (Array.isArray(raw?.data) ? raw.data : []);
    return {
      content: Array.isArray(content) ? content : [],
      totalElements: raw?.totalElements ?? raw?.total ?? (Array.isArray(content) ? content.length : 0),
    };
  };

  const fetchCombinedChallans = async () => {
    const preloadedCombinedData = appPreloader.getCachedData('combinedChallans');
    if (preloadedCombinedData) {
      return normalizeChallanList(preloadedCombinedData);
    }
    
    try {
      const combinedChallansResponse = await api.get("/challans/searchChallans?challanType=COMBINED&size=1000");
      const normalized = normalizeChallanList(combinedChallansResponse.data);
      appPreloader.cache.set('combinedChallans', normalized);
      appPreloader.saveToLocalStorage();
      return normalized;
    } catch (error) {
      console.error('Failed to fetch combined challans:', error);
      return { content: [], totalElements: 0 };
    }
  };

const fetchDashboardData = async () => {
    if (isFetchingDashboardRef.current) return;
    isFetchingDashboardRef.current = true;
    setLoadingDashboard(true);
    const startTime = Date.now();
    const MIN_SKELETON_TIME = 500; // 500ms minimum skeleton display time
    
    try {
      // Check if dashboard data is preloaded
      const preloadedDashboardData = appPreloader.getCachedData('dashboardData');
      
      let data, combinedData;
      
      if (preloadedDashboardData) {
        console.log('Using preloaded dashboard data');
        data = preloadedDashboardData;
        combinedData = await fetchCombinedChallans();
      } else {
        console.log('Fetching dashboard data from API');
        const response = await api.get("/challans/getDashboardData");
        const raw = response.data;
        // Support both a direct object and a { success, data: {...} } envelope.
        data = raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
          ? raw.data
          : raw;
        // Cache the dashboard data immediately
        appPreloader.cache.set('dashboardData', data);
        appPreloader.saveToLocalStorage();
        
        // Fetch combined challans
        combinedData = await fetchCombinedChallans();
      }

      // Aggregate combined challan items by product name
      const combinedProductSummary = (combinedData.content || []).reduce((acc, challan) => {
        (challan.items || []).forEach(item => {
          acc[item.name] = (acc[item.name] || 0) + Number(item.deliveredQty || 0);
        });
        return acc;
      }, {});

      // Fetch real-time financial data, needs attention, and cylinder tracking in parallel
      const [financialSummary, attentionData, cylinderData] = await Promise.all([
        financialDashboardApi.getFinancialSummary(),
        financialDashboardApi.getNeedsAttention(),
        financialDashboardApi.getCylinderTrackingSummary()
      ]);

      setNeedsAttention(attentionData);
      setCylinderTracking(cylinderData);
      setRecentInvoices(financialSummary.recentInvoices || []);
      setRecentBills(financialSummary.recentBills || []);

      const newStats = [
        { ...initialStats[0], value: data.totalDeliveredChallans },
        { ...initialStats[1], value: data.totalReceivedChallans },
        // Use totalElements from the search API for combined challans
        { ...initialStats[2], value: combinedData.totalElements || 0 },
        { ...initialStats[3], value: data.totalCustomers },
        // Real financial data
        { 
          ...initialStats[4], 
          value: financialSummary.totalReceivable,
          subtext: `₹${Number(financialSummary.overdueAmount || 0).toLocaleString('en-IN')} overdue`,
          delta: financialSummary.totalReceivable > 0 ? `${((financialSummary.overdueAmount || 0) / financialSummary.totalReceivable * 100).toFixed(1)}%` : "0%",
          up: true
        },
        { 
          ...initialStats[5], 
          value: financialSummary.totalPayable,
          subtext: `${financialSummary.openBillsCount} open bills`,
          delta: "",
          up: true
        },
        { 
          ...initialStats[6], 
          value: financialSummary.totalSales,
          subtext: "vs. last month",
          delta: `${financialSummary.salesGrowth >= 0 ? '+' : ''}${financialSummary.salesGrowth}%`,
          up: financialSummary.salesGrowth >= 0
        },
        { 
          ...initialStats[7], 
          value: financialSummary.totalPurchases,
          subtext: "vs. last month",
          delta: `${financialSummary.purchasesGrowth >= 0 ? '+' : ''}${financialSummary.purchasesGrowth}%`,
          up: financialSummary.purchasesGrowth >= 0
        },
      ];
      setStats(newStats.filter((s) => !s.planRequired || planAtLeast(currentPlan, s.planRequired)));
     //const challanCards = stats.filter(s => s.type === 'challan');
 //const financeCards = stats.filter(s => s.type === 'finance');
      // Merge combined summary with the main product summaries
      const mergedSummaries = (data.productSummaries || []).map(p => {
        const totalCombined = combinedProductSummary[p.productName] || 0;
        return {
          name: p.productName,
          delivered: p.totalDelivered,
          received: p.totalReceived,
          combined: totalCombined,
          total: p.totalDelivered + p.totalReceived + totalCombined,
          // For chart
          productName: p.productName,
          totalDelivered: p.totalDelivered,
          totalReceived: p.totalReceived,
        };
      });

      setProductSummaries(mergedSummaries);
      setTopProducts(mergedSummaries.slice(0, 5)); // Show top 5 products in the table

      // Fetch customer summaries on initial load
      fetchCustomerSummaries().then(() => {
        // Pre-fetch metrics for first 4 visible customers
        loadVisibleCustomerMetrics();
      }).catch(error => {
        console.error('Failed to fetch customer summaries:', error);
      });
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      // Set fallback values so UI doesn't show empty states
      setNeedsAttention({
        overdueInvoices: { count: 0, amount: 0 },
        cylindersNotReturned: { count: 0, customers: 0 },
        cylindersTestingDue: { count: 0 },
        challansAwaitingCompletion: { count: 0, oldestDays: 0 }
      });
      setCylinderTracking({
        deliveredCylinders: 0,
        deliveredToday: 0,
        receivedCylinders: 0,
        receivedToday: 0,
        combinedCylinders: 0,
        combinedToday: 0,
        availableStock: 0,
        totalStock: 0,
        capacityPercentage: 0,
        withCustomer: 0,
        inTransit: 0,
        testingDue: 0
      });
      setRecentInvoices([]);
      setRecentBills([]);
    } finally {
      isFetchingDashboardRef.current = false;
      // Ensure skeleton shows for at least MIN_SKELETON_TIME
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_SKELETON_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        setTimeout(() => {
          setLoadingDashboard(false);
        }, remainingTime);
      } else {
        setLoadingDashboard(false);
      }
    }
  };


  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCustomersPopup) return;

    if (
      customerSummaries.length === 0 ||
      loadingCustomers
    ) {
      return;
    }

    loadVisibleCustomerMetrics();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showCustomersPopup,
    customerPage,
    customerSummaries.length,
    loadingCustomers
  ]);

  const handleRefreshClick = async () => {
    setIsRefreshing(true);

    // Reset customer metrics loaded flags
    setCustomerSummaries(prev =>
      prev.map(customer => ({
        ...customer,
        metricsLoaded: false,
        balance: null,
        totalTransactions: null,
        delivered: null,
        received: null
      }))
    );

    // Clear cache to force fresh data fetch
    appPreloader.cache.delete('dashboardData');
    appPreloader.cache.delete('combinedChallans');
    appPreloader.cache.delete('customerSummaries');

    // Clear customer activity cache
    for (const key of appPreloader.cache.keys()) {
      if (key.startsWith("customerActivity_")) {
        appPreloader.cache.delete(key);
      }
    }

    appPreloader.saveToLocalStorage();

    // Reset fetch flags to allow re-fetching
    isFetchingDashboardRef.current = false;
    isFetchingCustomersRef.current = false;

    await fetchDashboardData();
    
    // Force refresh customer summaries with fresh running balances
    await fetchCustomerSummaries(true);
    
    // Also refresh WhatsApp credits
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await fetch(`${BASE_URL}/client-config/current/whatsapp-credits`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const creditsValue = data?.credits || data?.value || data?.count || data;
          // Update the cache
          appPreloader.cache.set('whatsappCredits', creditsValue);
          // Dispatch a custom event to notify Header component
          window.dispatchEvent(new CustomEvent('whatsappCreditsUpdated', { detail: creditsValue }));
        }
      }
    } catch (error) {
      console.error('Failed to refresh WhatsApp credits:', error);
    }
    
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const chartHeight = window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 140 : 160;
  const chartTickSize = window.innerWidth < 768 ? 9 : 11;
  const chartYAxisWidth = window.innerWidth < 768 ? 20 : 24;
  const chartMaxBarSize = window.innerWidth < 768 ? 10 : 14;

 // const formatRupee = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;
  //const challanCards = stats.filter(s => s.type === 'challan');
  //const financeCards = stats.filter(s => s.type === 'finance');

  return (
    <div className="dashboardContainer">      
      <div className="dashboardContent">
      {greetingBurstActive && (
        <div className="greetingBurstOverlay" aria-hidden="true">
          {greetingBurstItems.map((item) => (
            <span
              key={item.id}
              className="greetingBurstEmoji"
              style={{
                left: `${item.left}px`,
                top: `${item.top}px`,
                fontSize: `${item.size}px`,
                animationDelay: `${item.delay}ms`,
                animationDuration: `${item.duration}ms`,
                "--burst-x": `${item.x}px`,
                "--burst-y": `${item.y}px`,
              }}
            >
              {item.emoji}
            </span>
          ))}
        </div>
      )}
      <div className="dashboardTopBar">
        <div className="dashboardSectionTabs">
          <Tabs
            value={activeSection}
            onChange={(event, newValue) => setActiveSection(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ '& .MuiTabs-scrollButtons': { display: 'flex' } }}
          >
            <Tab label="CTS Dashboard" />
            {canUseCrm && <Tab label="CRM Dashboard" />}
          </Tabs>
        </div>
        <div className="dashboardGreeting">
          <span
            className={`dateText ${greetingBurstActive ? "flowStripDateBurst" : ""}`}
            onDoubleClick={handleDateDoubleClick}
            style={{ cursor: 'pointer' }}
          >
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
          <span className="flowStripMessage">— {getTimeBasedMessage()}</span>
        </div>
      </div>
      {/* header */}
      <div className="header">
        <div className="headerLeft">
          <div className="headerBrandRow">
            <div className="headerBrand">Zerlak One · Operations</div>
          </div>
          <div className="headerTitle">{activeSection === 1 ? 'CRM Dashboard' : 'Dashboard'}</div>
          <div className="headerSubtitle">
            {activeSection === 1
              ? 'Sales pipeline overview — leads, opportunities & conversions'
              : getHeaderSubtitle()}
          </div>
        </div>
        <div className="headerRight">
          <div className="headerActions">
            {activeSection === 1 ? (
              <>
                <button className="refreshButton" onClick={() => setCrmRefreshKey((k) => k + 1)}>
                  <RefreshCw size={15} />
                  Refresh
                </button>
                <button className="refreshButton refreshButtonPrimary" onClick={() => navigate('/crm/leads/new')}>
                  <Plus size={15} />
                  New Lead
                </button>
              </>
            ) : (
              <button className="refreshButton" onClick={handleRefreshClick}>
                {isRefreshing ? <AnimatedRefreshIcon /> : <RefreshCw size={15} />}
                Refresh
              </button>
            )}
          </div>
        </div>
      </div>

      {activeSection === 0 && (
        <>
      {/* stat cards - flat, left-border accent, mono numerals */}
      <div className="statGrid">
        {loadingDashboard ? (
          // Skeleton for stat cards
          [...Array(4)].map((_, index) => (
            <Box key={index} className="statCard">
              <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '8px', mb: 2 }} />
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          ))
        ) : (
          stats.map((s) => (
            <div 
              key={s.label} 
              onClick={() => {
                if (s.filter) {
                  navigate(`${s.route}?challanType=${s.filter}`);
                } else {
                  navigate(s.route);
                }
              }}
              className={`statCard ${s.accent === '#0F6E56' ? 'accentGreen' : s.accent === '#B45309' ? 'accentOrange' : s.accent === '#5B4B8A' ? 'accentPurple' : 'accentBlue'}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div className="statCardHeader">
                <div className={`statCardIcon ${s.accent === '#0F6E56' ? 'bgAccentGreen' : s.accent === '#B45309' ? 'bgAccentOrange' : s.accent === '#5B4B8A' ? 'bgAccentPurple' : 'bgAccentBlue'}`}>
                  <s.Icon size={17} color={s.accent} />
                </div>
                <div className={`statCardTrend ${s.up ? 'statCardTrendUp' : 'statCardTrendDown'}`}>
                  {s.up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{s.delta}
                </div>
              </div>
              <div className="statCardValue">
              {s.isRupee ? `₹${Number(s.value).toLocaleString('en-IN')}` : s.value}
              
            </div>
              <div className="statCardLabel">{s.label}</div>

              {s.isRupee && (
                <button className="finance-viewall-btn" onClick={(e) => {
                  e.stopPropagation();
                  navigate(s.route || '/purchases');
                }}>
                  View All <span>→</span>
                </button>
              )}
            </div>
            
          ))
        )}
        
      </div>
   <div className="top-two-grid">

  {/* LEFT - CYLINDER TRACKING */}
  <div className="cylinder-tracking-section">
    <div className="section-header">
      <h3>CYLINDER TRACKING</h3>
      <span className="view-link" onClick={() => navigate('/dashboard/cylinders/cylinderdashboard')}>
        View Cylinder Dashboard →
      </span>
    </div>
    <p className="section-sub">Live inventory and movement across all locations</p>

    <div className="cylinder-cards-grid">
      <div className="cyl-card">
        <div className="cyl-icon blue"><Truck size={18} /></div>
        <div className="cyl-content">
          <div className="cyl-label">Delivered Cylinders</div>
          <div className="cyl-value">{cylinderTracking ? Number(cylinderTracking.deliveredCylinders || 0).toLocaleString() : '—'}</div>
          <div className="cyl-sub">+{cylinderTracking ? Number(cylinderTracking.deliveredToday || 0).toLocaleString() : '0'} today</div>
        </div>
      </div>
      <div className="cyl-card">
        <div className="cyl-icon green"><Check size={18} /></div>
        <div className="cyl-content">
          <div className="cyl-label">Received Cylinders</div>
          <div className="cyl-value">{cylinderTracking ? Number(cylinderTracking.receivedCylinders || 0).toLocaleString() : '—'}</div>
          <div className="cyl-sub">+{cylinderTracking ? Number(cylinderTracking.receivedToday || 0).toLocaleString() : '0'} today</div>
        </div>
      </div>
      <div className="cyl-card">
        <div className="cyl-icon orange"><Cylinder size={18} /></div>
        <div className="cyl-content">
          <div className="cyl-label">Combined Cylinders</div>
          <div className="cyl-value">{cylinderTracking ? Number(cylinderTracking.combinedCylinders || 0).toLocaleString() : '—'}</div>
          <div className="cyl-sub">+{cylinderTracking ? Number(cylinderTracking.combinedToday || 0).toLocaleString() : '0'} today</div>
        </div>
      </div>
      <div className="cyl-card">
        <div className="cyl-icon gray"><BoxIcon size={18} /></div>
        <div className="cyl-content">
          <div className="cyl-label">Available Stock Cylinders</div>
          <div className="cyl-value">{cylinderTracking ? Number(cylinderTracking.availableStock || 0).toLocaleString() : '—'}</div>
          <div className="cyl-sub">{cylinderTracking ? cylinderTracking.capacityPercentage : '0'}% capacity</div>
        </div>
      </div>
    </div>

    <div className="stock-health">
      <div className="health-left">
        <div className="health-label">Current stock health</div>
        <div className="health-value">
          {cylinderTracking 
            ? `${Number(cylinderTracking.availableStock || 0).toLocaleString()} of ${Number(cylinderTracking.totalStock || 0).toLocaleString()} cylinders available`
            : 'Loading...'
          }
        </div>
      </div>
      <div className="health-bar-wrap">
        <div className="health-bar"><div className="health-fill" style={{width: cylinderTracking ? `${cylinderTracking.capacityPercentage}%` : '0%'}}></div></div>
      </div>
      <div className="health-percent">{cylinderTracking ? cylinderTracking.capacityPercentage : '0'}%</div>
    </div>
  </div>

  {/* RIGHT - NEEDS ATTENTION */}
  <div className="needs-attention-box">
    <div className="section-header">
      <h3>NEEDS ATTENTION</h3>
      <span className="view-link" onClick={() => navigate('/attention')}>View all →</span>
    </div>
    <div className="attention-list">
      <div className="attention-item">
        <div className="att-icon red"><AlertTriangle size={16} /></div>
        <div className="att-content">
          <div className="att-title">
            {needsAttention ? needsAttention.overdueInvoices.count : '—'} overdue invoices
          </div>
          <div className="att-sub">
            ₹{needsAttention ? Number(needsAttention.overdueInvoices.amount || 0).toLocaleString('en-IN') : '0'} pending collection
          </div>
        </div>
        <ChevronRight size={14} className="att-arrow" />
      </div>
      <div className="attention-item">
        <div className="att-icon yellow"><Clock size={16} /></div>
        <div className="att-content">
          <div className="att-title">
            {needsAttention ? needsAttention.cylindersNotReturned.count : '—'} cylinders not returned
          </div>
          <div className="att-sub">
            Due from {needsAttention ? needsAttention.cylindersNotReturned.customers : '0'} customers
          </div>
        </div>
        <ChevronRight size={14} className="att-arrow" />
      </div>
      <div className="attention-item">
        <div className="att-icon yellow"><Clock size={16} /></div>
        <div className="att-content">
          <div className="att-title">
            {needsAttention ? needsAttention.cylindersTestingDue.count : '—'} cylinders testing due
          </div>
          <div className="att-sub">
            Within the next 15 days
          </div>
        </div>
        <ChevronRight size={14} className="att-arrow" />
      </div>
      <div className="attention-item">
        <div className="att-icon blue"><Clock size={16} /></div>
        <div className="att-content">
          <div className="att-title">
            {needsAttention ? needsAttention.challansAwaitingCompletion.count : '—'} challans awaiting completion
          </div>
          <div className="att-sub">
            Oldest is {needsAttention ? needsAttention.challansAwaitingCompletion.oldestDays : '0'} days
          </div>
        </div>
        <ChevronRight size={14} className="att-arrow" />
      </div>
    </div>
  </div>

  <div className="recent-two-grid">

  {canUseFinance && (
  <>
  {/* LEFT - RECENT INVOICES */}
  <div className="recent-card">
    <div className="recent-header">
      <h3>RECENT INVOICES</h3>
      <span className="view-link" onClick={() => navigate('/dashboard/recurring-list')}>View all →</span>
    </div>
    <div className="recent-table-wrap">
      <table className="recent-table">
        <thead>
          <tr>
            <th>INVOICE #</th><th>CUSTOMER</th><th>DATE</th><th>AMOUNT</th><th>STATUS</th><th></th>
          </tr>
        </thead>
        <tbody>
          {recentInvoices.length > 0 ? recentInvoices.map((inv, index) => (
            <tr key={inv.id || index}>
              <td className="link">{inv.invoiceNumber || `INV-${index + 1}`}</td>
              <td>{inv.customerName || 'Unknown'}</td>
              <td>{inv.date ? new Date(inv.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
              <td className="bold">₹{Number(inv.amount || 0).toLocaleString('en-IN')}</td>
              <td>
                <span className={`status-dot ${getStatusClass(inv.status)}`}>
                  {inv.status || 'Unknown'}
                </span>
              </td>
              <td>...</td>
            </tr>
          )) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                No recent invoices
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>

  {/* RIGHT - RECENT BILLS */}
  <div className="recent-card">
    <div className="recent-header">
      <h3>RECENT BILLS</h3>
      <span className="view-link" onClick={() => navigate('/dashboard/bills')}>View all →</span>
    </div>
    <div className="recent-table-wrap">
      <table className="recent-table">
        <thead>
          <tr>
            <th>BILL #</th><th>VENDOR</th><th>DATE</th><th>AMOUNT</th><th>STATUS</th><th></th>
          </tr>
        </thead>
        <tbody>
          {recentBills.length > 0 ? recentBills.map((bill, index) => (
            <tr key={bill.id || index}>
              <td className="link">{bill.billNumber || `BILL-${index + 1}`}</td>
              <td>{bill.vendorName || 'Unknown'}</td>
              <td>{bill.date ? new Date(bill.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
              <td className="bold">₹{Number(bill.amount || 0).toLocaleString('en-IN')}</td>
              <td>
                <span className={`status-dot ${getStatusClass(bill.status)}`}>
                  {bill.status || 'Unknown'}
                </span>
              </td>
              <td>...</td>
            </tr>
          )) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                No recent bills
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>

  </>
)}

</div>

</div>

      {/* two column: top products + customer summary */}
      <div className="twoColumnGrid">
        <div className="card">
          <div className="sectionHeader">
            <h3 className="sectionTitle">
              Top products
            </h3>
            <button
              onClick={() => setShowProductsPopup(true)}
              className="sectionLink"
            >
              View all
              <span>→</span>
            </button>
          </div>
          <div className="tableContainer">
            {loadingDashboard ? (
              <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="text" width="15%" height={24} />
                    <Skeleton variant="text" width="15%" height={24} />
                    <Skeleton variant="text" width="15%" height={24} />
                    <Skeleton variant="text" width="15%" height={24} />
                  </Box>
                ))}
              </Box>
            ) : (
              <table className="table">
                <thead>
                  <tr className="tableHeader">
                    <th className="tableHeaderCell">Product</th>
                    <th className="tableHeaderCell tableHeaderCellCenter">Delivered</th>
                    <th className="tableHeaderCell tableHeaderCellCenter">Received</th>
                    <th className="tableHeaderCell tableHeaderCellCenter">Combined</th>
                    <th className="tableHeaderCell tableHeaderCellCenter">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((c, index) => (
                    <tr key={c.name} className="tableRow">
                      <td className="tableCell">
                        <div className="productInfo">
                          <span className="productName">
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td className="tableCell">
                        <span className="deliveredValue">
                          {c.delivered}
                        </span>
                      </td>
                      <td className="tableCell">
                        <span className="receivedValue">
                          {c.received}
                        </span>
                      </td>
                      <td className="tableCell">
                        <span className="combinedValue">
                          {c.combined}
                        </span>
                      </td>
                      <td className="tableCell">
                        <span className="totalValue">
                          {c.total}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card">
          <div className="sectionHeader">
            <h3 className="sectionTitle">
              Customer summary
            </h3>
            {/* <div className="cardSubtitle">Customer activity overview</div> */}
            <button
              onClick={handleOpenCustomersPopup}
              className="sectionLink"
            >
              View all
              <span>→</span>
            </button>
          </div>
          {loadingDashboard ? (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Skeleton variant="rectangular" width="50%" height={60} sx={{ borderRadius: '8px' }} />
                <Skeleton variant="rectangular" width="50%" height={60} sx={{ borderRadius: '8px' }} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={chartHeight} sx={{ borderRadius: '8px' }} />
            </Box>
          ) : (
            <>
              <div className="customerSummaryGrid">
                <div className="customerSummaryCard">
                  <div className="customerSummaryLabel">Active customers</div>
                  <div className="customerSummaryValue">{customerSummaries.filter(c => c.metricsLoaded && (c.totalTransactions > 0 || c.balance > 0 || c.delivered > 0 || c.received > 0)).length}</div>
                </div>
                <div className="customerSummaryCard">
                  <div className="customerSummaryLabel">Total customers</div>
                  <div className="customerSummaryValue">{customerSummaries.length}</div>
                </div>
              </div>
              <div className="chartDivider" />
              <div style={{ marginTop: 16, overflow: "visible", position: "relative" }}>
                <div className="chartLabel">Delivered vs received by product</div>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={productSummaries}
                    barGap={2}
                    margin={{
                      top: 20, right: 20, left: 10, bottom: 0,
                    }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F4" vertical={false} />
                    <XAxis 
                      dataKey="productName" 
                      tick={{ fontSize: chartTickSize, fill: "#94A3B8" }} 
                      axisLine={{ stroke: "#E2E6EA" }} 
                      tickLine={false} 
                    />
                    <YAxis 
                      tick={{ fontSize: chartTickSize, fill: "#94A3B8" }} 
                      axisLine={false} 
                      tickLine={false} 
                      width={chartYAxisWidth} 
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "#F4F6F8" }}
                      wrapperStyle={{
                        zIndex: 9999,
                        overflow: "visible",
                      }} />
                    <Bar dataKey="totalDelivered" name="Delivered" fill="#0F6E56" radius={[3, 3, 0, 0]} maxBarSize={chartMaxBarSize} />
                    <Bar dataKey="totalReceived" name="Received" fill="#B45309" radius={[3, 3, 0, 0]} maxBarSize={chartMaxBarSize} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Products Popup */}
      {showProductsPopup && createPortal(
        <div className="popupOverlay" onClick={(e) => e.target === e.currentTarget && setShowProductsPopup(false)}>
          <div className="popupModal" role="dialog" aria-modal="true" aria-label="All Products">
            <div className="popupHeader">
              <div className="popupTitle">
                All Products
              </div>
              <button
                onClick={() => setShowProductsPopup(false)}
                className="popupCloseButton"
              >
                <X size={20} color="#64748B" />
              </button>
            </div>
            <div className="popupContent">
              <div className="popupSection">
                <div className="popupSectionLabel">
                  Gas Products
                </div>
                <div className="popupGrid">
                  {productSummaries.filter(p => ["A1", "A2", "A3"].includes(p.productName)).map((product) => (
                    <div key={product.productName} className="popupItemCard">
                      <div className="popupItemName">
                        {product.productName}
                      </div>
                      <div className="popupItemStat">
                        Delivered: <span className="popupItemStatValue popupItemStatValueDelivered">{product.totalDelivered}</span>
                      </div>
                      <div className="popupItemStat">
                        Received: <span className="popupItemStatValue popupItemStatValueReceived">{product.totalReceived}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="popupSection">
                <div className="popupSectionLabel">
                  Other Products
                </div>
                <div className="popupGrid">
                  {productSummaries.filter(p => !["A1", "A2", "A3"].includes(p.productName)).map((product) => (
                    <div key={product.productName} className="popupItemCard">
                      <div className="popupItemName">
                        {product.productName}
                      </div>
                      <div className="popupItemStat">
                        Delivered: <span className="popupItemStatValue popupItemStatValueDelivered">{product.totalDelivered}</span>
                      </div>
                      <div className="popupItemStat">
                        Received: <span className="popupItemStatValue popupItemStatValueReceived">{product.totalReceived}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Customers Popup */}
      {showCustomersPopup && createPortal(
        <div className="popupOverlay" onClick={(e) => e.target === e.currentTarget && setShowCustomersPopup(false)}>
          <div className="popupModal popupModalLarge" role="dialog" aria-modal="true" aria-label="All Customers">
            <div className="popupHeader">
              <div className="popupTitle">
                All Customers
              </div>
              <button
                onClick={() => setShowCustomersPopup(false)}
                className="popupCloseButton"
              >
                <X size={20} color="#64748B" />
              </button>
            </div>
            <div className="popupContent">
              <div className="popupSection">
                <div className="popupSectionLabel">
                  All Customers
                </div>
                {loadingCustomers ? (
                  <div className="loadingState">
                    <div className="loadingText">Loading customers...</div>
                  </div>
                ) : customerSummaries.length === 0 ? (
                  <div className="emptyState">
                    <div className="emptyText">No customers found</div>
                  </div>
                ) : (
                  <>
                    <div className={`customerListGrid ${customerSummaries.length <= 4 ? 'customerListGridAdaptive' : ''}`}>
                      {customerSummaries.length <= 4 ? customerSummaries.map((customer) => (
                        <div key={customer.id} className="customerCard">
                          <div className="customerCardHeader">
                            <div className="customerAvatar">
                              {customer.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="customerCardInfo">
                              <div className="customerCardName">
                                {customer.name}
                              </div>
                              {customer.metricsLoaded && (customer.totalTransactions > 0 || customer.balance > 0 || customer.delivered > 0 || customer.received > 0) && (
                                <div className="activeTag">Active</div>
                              )}
                            </div>
                            <button
                              className="passbookButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewPassbook(customer.id, customer.name, customer.email);
                              }}
                              title="View Passbook"
                            >
                              <BsJournalBookmarkFill size={26} />
                            </button>
                          </div>
                          <div className="customerCardBody" onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                            <div className="customerDetailRow">
                              <Phone size={14} className="customerDetailIcon" />
                              <span className="customerDetailText">{customer.mobile}</span>
                            </div>
                            <div className="customerDetailRow">
                              <Mail size={14} className="customerDetailIcon" />
                              <span className="customerDetailText">{customer.email}</span>
                            </div>
                            <div className="customerDetailRow">
                              <MapPin size={14} className="customerDetailIcon" />
                              <span
                                  className="customerDetailText customerAddressText"
                                  data-tooltip={customer.address || "N/A"}
                                  onMouseEnter={showAddressTooltip}
                                  onMouseLeave={hideAddressTooltip}
                                >
                                  {customer.address || "N/A"}
                                </span>
                            </div>
                          </div>
                          <div 
                            className={`customerCardFooter ${Math.abs(Number(customer.runningBalance || 0)).toLocaleString("en-IN").replace(/,/g, '').length > 5 ? 'largeValues' : ''}`} 
                            onClick={() => navigate(`/dashboard/customers/${customer.id}`)}
                          >
                            <div className="customerStat">
                              <div className="customerStatLabel">Running Balance</div>
                              <div className={`customerStatValue customerStatBalance ${Number(customer.runningBalance || 0) < 0 ? 'negative' : ''}`}>
                                <CurrencyRupee sx={{ fontSize: 12, color: Number(customer.runningBalance || 0) < 0 ? "#DC2626" : "#16a34a", mr: 0.25 }} />
                                {Number(customer.runningBalance || 0).toLocaleString("en-IN")}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Quantity Balance</div>
                              <div className="customerStatValue customerStatBalance">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : Number(customer.balance || 0).toLocaleString("en-IN")}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Transactions</div>
                              <div className="customerStatValue">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : customer.totalTransactions ?? "-"}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Delivered</div>
                              <div className="customerStatValue customerStatDelivered">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : customer.delivered ?? "-"}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Received</div>
                              <div className="customerStatValue customerStatReceived">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : customer.received ?? "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )) : getCurrentPageCustomers().map((customer) => (
                        <div key={customer.id} className="customerCard">
                          <div className="customerCardHeader">
                            <div className="customerAvatar">
                              {customer.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="customerCardInfo">
                              <div className="customerCardName">
                                {customer.name}
                              </div>
                              {customer.metricsLoaded && (customer.totalTransactions > 0 || customer.balance > 0 || customer.delivered > 0 || customer.received > 0) && (
                                <div className="activeTag">Active</div>
                              )}
                            </div>
                            <button
                              className="passbookButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewPassbook(customer.id, customer.name, customer.email);
                              }}
                              title="View Passbook"
                            >
                              <BsJournalBookmarkFill size={26} />
                            </button>
                          </div>
                          <div className="customerCardBody" onClick={() => navigate(`/dashboard/customers/${customer.id}`)}>
                            <div className="customerDetailRow">
                              <Phone size={14} className="customerDetailIcon" />
                              <span className="customerDetailText">{customer.mobile}</span>
                            </div>
                            <div className="customerDetailRow">
                              <Mail size={14} className="customerDetailIcon" />
                              <span className="customerDetailText">{customer.email}</span>
                            </div>
                            <div className="customerDetailRow">
                              <MapPin size={14} className="customerDetailIcon" />
                              <span
                                  className="customerDetailText customerAddressText"
                                  data-tooltip={customer.address || "N/A"}
                                  onMouseEnter={showAddressTooltip}
                                  onMouseLeave={hideAddressTooltip}
                                >
                                  {customer.address || "N/A"}
                                </span>
                            </div>
                          </div>
                          <div 
                            className={`customerCardFooter ${Math.abs(Number(customer.runningBalance || 0)).toLocaleString("en-IN").replace(/,/g, '').length > 5 ? 'largeValues' : ''}`} 
                            onClick={() => navigate(`/dashboard/customers/${customer.id}`)}
                          >
                            <div className="customerStat">
                              <div className="customerStatLabel">Running Balance</div>
                              <div className={`customerStatValue customerStatBalance ${Number(customer.runningBalance || 0) < 0 ? 'negative' : ''}`}>
                                <CurrencyRupee sx={{ fontSize: 12, color: Number(customer.runningBalance || 0) < 0 ? "#DC2626" : "#16a34a", mr: 0.25 }} />
                                {Number(customer.runningBalance || 0).toLocaleString("en-IN")}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Quantity Balance</div>
                              <div className="customerStatValue customerStatBalance">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : Number(customer.balance || 0).toLocaleString("en-IN")}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Transactions</div>
                              <div className="customerStatValue">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : customer.totalTransactions ?? "-"}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Delivered</div>
                              <div className="customerStatValue customerStatDelivered">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : customer.delivered ?? "-"}
                              </div>
                            </div>
                            <div className="customerStat">
                              <div className="customerStatLabel">Received</div>
                              <div className="customerStatValue customerStatReceived">
                                {customerMetricsLoading[customer.id]
                                  ? "..."
                                  : customer.received ?? "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {customerSummaries.length > 4 && (
                      <div className="customerPagination">
                        <button 
                          className="paginationButton"
                          onClick={() => handleCustomerPageChange(customerPage - 1)}
                          disabled={customerPage === 0}
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <span className="paginationInfo">
                          {customerPage + 1} of {getTotalCustomerPages()}
                        </span>
                        <button 
                          className="paginationButton"
                          onClick={() => handleCustomerPageChange(customerPage + 1)}
                          disabled={customerPage === getTotalCustomerPages() - 1}
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
        {addressTooltipPortal}
        </>
      )}
      {activeSection === 1 && canUseCrm && <CRMDashboard key={crmRefreshKey} embedded />}
      </div> {/* dashboardContent */}
    </div>
  );
}

